import React from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../contexts/LanguageContext';
import { getSEOLanguage } from '../utils/seoHelpers';

// Importar las traducciones directamente para SEO
import esTranslations from '../locales/es.json';
import caTranslations from '../locales/ca.json';

const translations = {
    es: esTranslations,
    ca: caTranslations
};

/**
 * Componente para gestionar meta tags SEO y Open Graph
 * Lógica: EN o CA → usa catalán para SEO | ES → usa español para SEO
 *
 * @param {Object} props
 * @param {string} props.pageKey - Clave de la página en meta.pages (ej: 'home', 'about')
 * @param {string} props.title - Título personalizado (opcional, sobrescribe pageKey)
 * @param {string} props.description - Descripción personalizada (opcional)
 * @param {string} props.image - URL de la imagen para OG (opcional)
 * @param {string} props.type - Tipo de contenido OG (por defecto: 'website')
 * @param {string} props.url - URL canónica de la página (opcional)
 */
const SEOMetaTags = ({
    pageKey,
    title: customTitle,
    description: customDescription,
    image,
    type = 'website',
    url
}) => {
    const { language } = useLanguage();

    // Determinar el idioma SEO según la lógica
    const seoLanguage = getSEOLanguage(language);
    const seoTranslations = translations[seoLanguage];

    // Obtener el título
    let title = customTitle;
    if (!title && pageKey) {
        const keys = `meta.pages.${pageKey}`.split('.');
        let value = seoTranslations;

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                value = null;
                break;
            }
        }

        title = value || seoTranslations.meta.title;
    }

    if (!title) {
        title = seoTranslations.meta.title;
    }

    // Descripción por defecto si no se proporciona
    const description = customDescription || title;

    // URL de la imagen OG - usa el logo de 3claus
    const ogImage = image || `${window.location.origin}/img/logo.png`;

    // URL canónica
    const canonicalUrl = url || window.location.href;

    return (
        <Helmet>
            {/* Meta tags básicos */}
            <html lang={seoLanguage} />
            <title>{title}</title>
            <meta name="description" content={description} />

            {/* Open Graph tags */}
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:site_name" content="3claus" />
            <meta property="og:locale" content={seoLanguage === 'ca' ? 'ca_ES' : 'es_ES'} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {/* URL canónica */}
            <link rel="canonical" href={canonicalUrl} />
        </Helmet>
    );
};

export default SEOMetaTags;
